import { Component, computed, input, model, output, ChangeDetectionStrategy } from "@angular/core";
import { Workflow } from "../service/Workflow";
import { AnswerType } from "../../data-ingestion/data.ingestion.service";
import type { FieldType } from "../../data-ingestion/data.ingestion.const";
import { WorkflowService } from "../service/workflow.service";
import { AlertService } from "../../../services/alert.service";
import { WorkflowUtil } from "../workflow.util";

@Component({
    template: "",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true,
})
export abstract class SystemActionComponent {
    protected abstract readonly actionCode: keyof typeof WorkflowService.SYSTEM_ACTIONS;

    readonly inputs = model.required<Workflow.WorkflowActionInput[]>();
    readonly inputIdGenerator = input.required<() => number>();
    readonly parentStage = input<Workflow.WorkflowStage | undefined>();

    readonly changed = output<void>();

    readonly inputMap = computed(() =>
        Object.fromEntries((this.inputs() ?? []).map((input) => [input.inputCode, input])),
    );

    protected readonly AnswerType = AnswerType;

    protected acceptedFor(inputCode: string): AnswerType[] | undefined {
        const meta = WorkflowService.SYSTEM_ACTIONS[this.actionCode];
        return "inputs" in meta ? meta.inputs?.find((i) => i.inputCode === inputCode)?.acceptedAnswerTypes : undefined;
    }

    protected acceptedFieldTypesFor(inputCode: string): FieldType[] | undefined {
        const meta = WorkflowService.SYSTEM_ACTIONS[this.actionCode];
        if (!("inputs" in meta)) return undefined;
        const inputDef = meta.inputs?.find((inputRow) => inputRow.inputCode === inputCode) as
            | Workflow.SystemActionInput
            | undefined;
        return inputDef?.acceptedFieldTypes;
    }

    setInputValue(inputCode: string, value: string) {
        if (
            inputCode === "useAssignmentPolicy" &&
            WorkflowUtil.isTruthyWorkflowCheckboxValue(value) &&
            this.parentStage()?.hasObjectApprovals
        ) {
            void AlertService.alert(
                "This stage uses Object Approvals. Disable Object Approvals on the stage before enabling Use Assignment Policy on an action.",
            );
            return;
        }
        if (!this.inputs()) {
            this.inputs.update(() => []);
        }
        let input = this.inputs().find((input) => input.inputCode === inputCode);
        if (!input) {
            input = {
                id: this.inputIdGenerator()(),
                inputCode: inputCode,
                inputValue: value,
            };
            this.inputs.update((inputs) => [...inputs, input!]);
        } else {
            input.inputValue = value;
        }
        this.changed.emit();
    }
}
